type WorkerPoolParams<T, TResult> = {
  items: T[];
  maxConcurrency: number;
  execute: (item: T) => Promise<TResult>;
  shouldStopScheduling?: () => boolean;
};

/**
 * 制限付き並列数でタスクを実行する
 * @param params 実行対象と並列数
 * @return 実行結果一覧
 */
export const runWithWorkerPool = async <T, TResult>({
  items,
  maxConcurrency,
  execute,
  shouldStopScheduling,
}: WorkerPoolParams<T, TResult>) => {
  const queue = [...items];
  const results: TResult[] = [];
  const workerCount = Math.min(queue.length, Math.max(1, maxConcurrency));

  if (workerCount === 0) {
    return results;
  }

  /**
   * 次に実行するタスクを取得する
   * @return 次のタスク
   */
  const takeNextItem = () => {
    if (shouldStopScheduling?.()) {
      return null;
    }

    return queue.shift() ?? null;
  };

  /**
   * ワーカー単位でキューを消費する
   * @return 実行処理の完了
   */
  const worker = async () => {
    while (true) {
      const item = takeNextItem();

      if (!item) {
        return;
      }

      results.push(await execute(item));
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return results;
};
